<onyx-loading-bar [visible]="!initialLoading() && loading()"></onyx-loading-bar>

<onyx-ribbon [marginBottom]="20">
  <ng-content select="[left]" left></ng-content>
  <ng-container right>
    <ng-content select="[right]"></ng-content>

    @if (views().length) {
      <onyx-dropdown
        [options]="views()"
        [value]="view()?.uuid"
        [ribbon]="true"
        [placeholder]="`${I18N}.views`"
        [showAddOption]="!!customViews()"
        [addOptionMessage]="`${I18N}.addView`"
        [optionTemplateRef]="viewOptionTemplate"
        width="max-content"
        [(attached)]="viewDropdownAttached"
        (selectedOptionChange)="setView($any($event.option)?.view ?? null)"
        (addOption)="addView()"
      >
        <onyx-icon ribbonIcon name="views-outlined" [size]="20"></onyx-icon>
      </onyx-dropdown>
    }
  </ng-container>
</onyx-ribbon>

<div
  class="table"
  [class.shadow]="tableShadow()"
  [class.border]="!tableShadow()"
  [style.height.px]="
    (showHeader() ? 32 : 0) +
    (pagination() ? 48 : 0) +
    bodyHeight() +
    (error() ? 64 : 0) +
    cdkVirtualScrollViewport.elementRef.nativeElement.offsetHeight -
    cdkVirtualScrollViewport.elementRef.nativeElement.clientHeight
  "
>
  <cdk-virtual-scroll-viewport
    [itemSize]="rowSize()"
    [appendOnly]="true"
    #cdkVirtualScrollViewport
  >
    <table>
      @if (showHeader()) {
        <thead>
          @let top = stickyTop;
          <tr>
            @if (selectionType() != null) {
              <th
                class="selection center frozen"
                [class.shadow]="hasFrozenShadows().left"
                [style.min-width.px]="SELECTION_COLUMN_WIDTH"
                [style.width.px]="SELECTION_COLUMN_WIDTH"
                [style.top.px]="top"
                [style.left.px]="0"
              >
                <div class="cell">
                  @if (data()?.items?.length) {
                    @switch (selectionType()) {
                      @case (OnyxTableSelectionType.CHECKBOX) {
                        <onyx-checkbox
                          [checked]="
                            isPageCheckboxChecked() ||
                            isPageCheckboxIndeterminate()
                          "
                          [indeterminate]="isPageCheckboxIndeterminate()"
                          (checkedChange)="togglePageItems(data()!, $event)"
                          [onyxTooltip]="
                            I18N +
                            '.batchActions' +
                            (isPageCheckboxChecked() ||
                            isPageCheckboxIndeterminate()
                              ? isPageCheckboxIndeterminate()
                                ? '.unselectPageSelected'
                                : '.unselectPage'
                              : '.selectPage')
                          "
                        ></onyx-checkbox>
                      }
                      @case (OnyxTableSelectionType.RADIO) {}
                    }
                  }
                </div>
              </th>
            }

            @for (column of visibleColumns(); track column.id) {
              <!-- This empty ng-container fixes ERROR Error: ASSERTION ERROR: Expected [Text|Element|Container|ElementContainer|Projection|IcuContainer] but got LetDeclaration. -->
              <ng-container></ng-container>
              @let frozen = $first && column.required;
              <th
                [class]="column.align ?? 'left'"
                [class.first]="$first"
                [class.last]="$last && !itemsRowOptions()"
                [class.frozen]="frozen"
                [class.shadow]="hasFrozenShadows().left"
                [style.min-width]="
                  column.width !== 'fill' ? column.width : undefined
                "
                [style.width]="column.width === 'fill' ? '100%' : undefined"
                [style.top.px]="top"
                [style.left.px]="frozen ? frozenRequiredLeft() : null"
              >
                <div
                  class="cell"
                  [onyxTooltip]="column.name ?? ''"
                  [onyxTooltipDisabled]="
                    overflowHelper.hasNoOverflow.bind(
                      overflowHelper,
                      columnTextElement
                    )
                  "
                >
                  @if (column.icon) {
                    <onyx-icon
                      class="f-muted"
                      [name]="column.icon.name"
                      [size]="column.icon.size"
                      [frame]="column.icon.frame"
                      [onyxTooltip]="column.tooltip ?? ''"
                    ></onyx-icon>
                  }

                  <p class="f-body-xs" #columnTextElement>
                    {{ column.name ?? '' | translate }}
                  </p>
                </div>
              </th>
            }

            @if (itemsRowOptions()) {
              <th
                class="last frozen"
                [class.shadow]="hasFrozenShadows().right"
                [style.min-width.px]="OPTIONS_COLUMN_WIDTH"
                [style.width.px]="OPTIONS_COLUMN_WIDTH"
                [style.top.px]="top"
                [style.right.px]="0"
              ></th>
            }
          </tr>
        </thead>
      }

      <tbody>
        <ng-container
          *cdkVirtualFor="
            let item of data()?.items;
            index as index;
            trackBy: itemIdentity.bind(this)
          "
        >
          <!-- This empty ng-container fixes ERROR Error: ASSERTION ERROR: Expected [Text|Element|Container|ElementContainer|Projection|IcuContainer] but got LetDeclaration. -->
          <ng-container></ng-container>
          @let id = getItemId(item);
          @let selected = selectedItemsId().includes(id);
          @let color = rowColor()?.({ item, index }) ?? OnyxTableColor.WHITE;
          @let options = itemsRowOptions()?.get(id);

          <tr
            class="{{ selected ? OnyxTableColor.BLUE : color }}"
            [class.hover]="showRowClick()"
            [style.height.px]="rowSize()"
            (click)="rowClick.emit({ item, index })"
          >
            @if (!initialLoading()) {
              @if (selectionType() != null) {
                @let disabled = selectionDisabled()?.({ item, index }) ?? false;
                <td
                  class="selection center frozen"
                  [class.shadow]="hasFrozenShadows().left"
                  [class.vertical-border]="showVerticalBorders()"
                  [style.min-width.px]="SELECTION_COLUMN_WIDTH"
                  [style.width.px]="SELECTION_COLUMN_WIDTH"
                  [style.left.px]="0"
                  (click)="
                    !disabled ? toggleItem(item, !selected, index) : null;
                    $event.stopPropagation()
                  "
                >
                  <div class="cell">
                    @switch (selectionType()) {
                      @case (OnyxTableSelectionType.CHECKBOX) {
                        <onyx-checkbox
                          [checked]="selected"
                          [disabled]="disabled"
                          (checkedChange)="
                            !disabled ? toggleItem(item, $event, index) : null
                          "
                          (click)="!disabled ? $event.stopPropagation() : null"
                        ></onyx-checkbox>
                      }
                      @case (OnyxTableSelectionType.RADIO) {
                        <ng-container [formGroup]="radioButtonSelection">
                          <onyx-radio-button
                            [formControl]="radioButtonSelection.controls.itemId"
                            [option]="id"
                            [disabled]="disabled"
                            (click)="
                              !disabled ? $event.stopPropagation() : null
                            "
                          ></onyx-radio-button>
                        </ng-container>
                      }
                    }
                  </div>
                </td>
              }

              @for (column of visibleColumns(); track column.id) {
                <!-- This empty ng-container fixes ERROR Error: ASSERTION ERROR: Expected [Text|Element|Container|ElementContainer|Projection|IcuContainer] but got LetDeclaration. -->
                <ng-container></ng-container>
                @let frozen = $first && column.required;
                <td
                  class="
                  {{ column.align ?? 'left' }} 
                  {{ column.color ? column.color({ item, index }) : '' }}
                  "
                  [class.first]="$first"
                  [class.last]="$last && !options"
                  [class.frozen]="frozen"
                  [class.shadow]="hasFrozenShadows().left"
                  [class.vertical-border]="showVerticalBorders()"
                  [style.min-width]="
                    column.width !== 'fill' ? column.width : undefined
                  "
                  [style.width]="column.width === 'fill' ? '100%' : undefined"
                  [style.left.px]="frozen ? frozenRequiredLeft() : null"
                >
                  <div class="cell">
                    <ng-container
                      *ngComponentOutlet="
                        column.component.ref;
                        inputs: column.component.inputs({
                          item,
                          index,
                        })
                      "
                    ></ng-container>
                  </div>

                  @if (column.actions.length) {
                    <div class="actions">
                      @for (action of column.actions; track action) {
                        @if (
                          action.hidden == null ||
                          !action.hidden({ item, index })
                        ) {
                          <onyx-icon-button
                            variant="transparent"
                            color="black"
                            [size]="24"
                            [onyxTooltip]="$any(action).tooltip"
                            (click)="
                              action.callback({ item, index });
                              $event.stopPropagation()
                            "
                          >
                            @let icon = action.icon;
                            <onyx-icon
                              [name]="$any(icon).name"
                              [size]="$any(icon).size"
                              [frame]="$any(icon).frame"
                            ></onyx-icon>
                          </onyx-icon-button>
                        }
                      }
                    </div>
                  }
                </td>
              }

              @if (options) {
                <td
                  class="center last frozen {{ OnyxTableColor.WHITE }}"
                  [class.shadow]="hasFrozenShadows().right"
                  [class.vertical-border]="showVerticalBorders()"
                  [style.min-width.px]="OPTIONS_COLUMN_WIDTH"
                  [style.width.px]="OPTIONS_COLUMN_WIDTH"
                  [style.right.px]="0"
                  (click)="$event.stopPropagation()"
                >
                  <div
                    class="cell"
                    tabindex="-1"
                    [onyxDropdownOptions]="options"
                    [onyxDropdownOptionTemplateRef]="rowOptionTemplateRef()"
                    (onyxDropdownSelectedValueChange)="$event.value!()"
                    [onyxDropdownDisabled]="!options.length"
                    onyxDropdownMaxHeight="fill"
                    (click)="$event.stopPropagation()"
                    (keydown.space)="$event.stopPropagation()"
                  >
                    <onyx-icon-button
                      data-testid="dropdown-actions-button"
                      variant="transparent"
                      color="black"
                      [size]="32"
                      icon="options-horizontal-filled"
                      [disabled]="!options.length"
                    ></onyx-icon-button>
                  </div>
                </td>
              }
            }
          </tr>
        </ng-container>
      </tbody>
    </table>
  </cdk-virtual-scroll-viewport>

  <div
    class="empty-body"
    [style.paddingTop.px]="showHeader() ? 32 : 0"
    [style.paddingBottom.px]="pagination() ? 48 : 0"
  >
    @if (initialLoading()) {
      <onyx-spinner [size]="32"></onyx-spinner>
    } @else if (data()?.items?.length === 0) {
      @let state = emptyState();
      @let emptyIcon = emptyStateIcon();
      <onyx-empty-state
        [heading]="state.title ?? DEFAULT_NOT_FOUND_TITLE"
        [description]="state.text"
        [action]="state.action"
        [pulse]="false"
      >
        @if (emptyIcon.emoji; as emoji) {
          <onyx-emoji [name]="emoji" [size]="32"></onyx-emoji>
        } @else {
          @let icon = emptyIcon.icon;
          <onyx-icon
            class="not-found-icon"
            [name]="icon.name"
            [size]="icon.size"
            [frame]="icon.frame"
          ></onyx-icon>
        }
      </onyx-empty-state>
    } @else if (error()) {
      <onyx-loading-error [(error)]="error"></onyx-loading-error>
    }
  </div>

  @if (pagination()) {
    <onyx-pagination
      [pagination]="pagination()!"
      [totalItems]="data()?.totalItems"
      [showLimit]="paginationShowLimit()"
      [padding]="paginationPadding()"
      (paginationChange)="pagination.set($event)"
    >
      <ng-content select="[statistics]"></ng-content>
    </onyx-pagination>
  }
</div>

<ng-content></ng-content>

@let showBatchActions =
  batchActionsTemplateRef() &&
  selectionType() === OnyxTableSelectionType.CHECKBOX &&
  selectedItemsId().length;

<div
  data-testid="batch-actions"
  class="batch-actions"
  [class.show]="showBatchActions"
  [style.--hide-duration.ms]="BATCH_ACTIONS_HIDE_DURATION"
>
  <p class="f-body-m f-medium">
    {{ `${I18N}.batchActions.selected` | translate }}
    {{ selectedItemsId().length | number }}
  </p>

  <onyx-icon class="arrow" name="arrow-right-outlined" [size]="20"></onyx-icon>

  <ng-container *ngTemplateOutlet="batchActionsTemplateRef()!"></ng-container>

  <onyx-icon-button
    data-testid="close-button"
    class="close"
    variant="primary"
    color="black"
    [size]="32"
    icon="close-outlined"
    [onyxTooltip]="`${I18N}.batchActions.unselectClose`"
    (click)="clearSelection()"
  ></onyx-icon-button>
</div>

<ng-template let-option="option" #viewOptionTemplate>
  <div class="view-option">
    <p>{{ option.name | translate }}</p>

    @let view = option.view;
    @if (!view.default) {
      <onyx-icon-button
        variant="transparent"
        color="black"
        [size]="24"
        icon="edit-outlined"
        [circle]="true"
        (click)="editView(view); $event.stopPropagation()"
      ></onyx-icon-button>
    }
  </div>
</ng-template>
